import { format } from 'date-fns'
import { Field, FieldArray, FieldArrayRenderProps, FormikHelpers, useFormikContext } from 'formik'
import React from 'react'
import 'react-calendar/dist/Calendar.css'
import 'react-clock/dist/Clock.css'
import 'react-datetime-picker/dist/DateTimePicker.css'
import { useNavigate, useParams } from 'react-router-dom'
import { toast } from 'react-toastify'
import * as Api from 'src/api'
import { Plus } from 'src/assets/icons/customIcons/Plus'
import { DeleteIcon } from 'src/assets/icons/customIcons/upload-icons/Delete'
import { useAdminAccess, useApi } from 'src/helpers/hooks'
import { useAuthenticatedHeaders } from 'src/hooks/auth/app'
import { useLocale } from 'src/hooks/locale/locale'
import { useTranslatable } from 'src/hooks/locale/utils'
import { Button } from 'src/tailwind/components/Button'
import { Section } from 'src/tailwind/components/Section'
import DateInput from 'src/views/components/forms/formik/DateInput'
import { Form } from 'src/views/components/forms/formik/Form'
import { FormError } from 'src/views/components/forms/formik/FormError'
import { FormSubmit } from 'src/views/components/forms/formik/FormSubmit'
import SelectInput from 'src/views/components/forms/formik/SelectInput'
import TextareaInput from 'src/views/components/forms/formik/TextareaInput'
import TextInput from 'src/views/components/forms/formik/TextInput'
import Loader from 'src/views/components/Loader'
import SuspenseWrapper from 'src/views/includes/SuspenseWrapper'
import Swal from 'sweetalert2'

const SWAL_CONFIG = {
  confirmButtonColor: '#0D6EFD',
  cancelButtonColor: '#6C757D',
} as const

// Types
interface SelectedOption {
  label: string
  value: string
}

interface FormValues {
  name: string
  showMessage?: string
  message: string | null
  type: string
  status: boolean
  allowLogin: string
  allowChoose: string
  schedule: string
  survey: string
  allowLecturerSurvey: string
  allowLecturerScore: string
  limitFunctional: string
  chooseAllowed: string
  chooseCriterias: Array<{
    fac_id: SelectedOption[]
    degree: SelectedOption[]
    sem: SelectedOption[]
    date_start: Date | string
    date_end: Date | string
  }>
}

interface FormContentProps {
  degreesOptions: SelectedOption[]
  semestersOptions: SelectedOption[]
  facultyOptions: SelectedOption[]
  isLoading: boolean
  regimeDefinitions: Api.RegimeDefinitions
}

// Custom hooks
function useRegimeData(id: string) {
  const headers = useAuthenticatedHeaders()

  const { data: regime } = useApi({
    endpoint: Api.getAdministratorRegimesShow,
    params: React.useMemo(
      () => ({
        headers,
        args: { id },
      }),
      [headers, id]
    ),
  })

  const { data: regimeDefinitions } = useApi({
    endpoint: Api.getRegimeDefinitions,
    params: React.useMemo(
      () => ({
        headers,
        args: { id },
      }),
      [headers, id]
    ),
  })

  return { regime, regimeDefinitions }
}

function useRegimeOptions(shouldFetch: boolean) {
  const headers = useAuthenticatedHeaders()

  const { data: degrees, isLoading: isLoadingDegrees } = useApi({
    endpoint: Api.getRegimeDegrees,
    params: React.useMemo(() => ({ headers }), [headers]),
    shouldFetch,
    suspense: false,
  })

  const { data: semesters, isLoading: isLoadingSemesters } = useApi({
    endpoint: Api.getAdministratorRegimesSemesters,
    params: React.useMemo(() => ({ headers }), [headers]),
    shouldFetch,
    suspense: false,
  })

  const { data: faculties, isLoading: isLoadingFaculties } = useApi({
    endpoint: Api.getRegimeFaculties,
    params: React.useMemo(() => ({ headers }), [headers]),
    shouldFetch,
    suspense: false,
  })

  const degreesOptions = React.useMemo(
    () => (degrees || []).map((degree) => ({ label: degree.name, value: degree.id })),
    [degrees]
  )

  const semestersOptions = React.useMemo(
    () => (semesters || []).map((semester) => ({ label: semester.toString(), value: semester.toString() })),
    [semesters]
  )

  const facultyOptions = React.useMemo(
    () => (faculties || []).map((faculty) => ({ label: faculty.name, value: faculty.id })),
    [faculties]
  )

  const isLoading = isLoadingDegrees || isLoadingSemesters || isLoadingFaculties

  return {
    degreesOptions,
    semestersOptions,
    facultyOptions,
    isLoading,
  }
}

function useFormSubmission(id: string) {
  const headers = useAuthenticatedHeaders()
  const navigate = useNavigate()
  const locale = useLocale()
  const t = useTranslatable()
  const [successfullySubmitted, setSuccessfullySubmitted] = React.useState(false)

  const onSubmit = React.useCallback(
    async (values: FormValues, formikHelpers: FormikHelpers<FormValues>) => {
      try {
        await Api.postAdministratorRegimesSave({
          headers,
          args: { id },
          body: {
            ...values,
            limitFunctional: values.limitFunctional === 'true' ? 1 : 0,
            chooseAllowed: values.chooseAllowed === 'true',
            showMessage: Number(values.showMessage) || 0,
            chooseCriterias: values.chooseCriterias.map((criteria) => ({
              ...criteria,
              fac_id: criteria.fac_id.map((item) => Number(item.value)),
              degree: criteria.degree.map((item) => Number(item.value)),
              sem: criteria.sem.map((item) => Number(item.value)),
              date_start: criteria.date_start ? format(criteria.date_start, 'yyyy-MM-dd HH:mm') : '',
              date_end: criteria.date_end ? format(criteria.date_end, 'yyyy-MM-dd HH:mm') : '',
            })),
          },
        })
        formikHelpers.resetForm()
        toast.success(t('admin_regimes:regime_updated_successfully'))
        setSuccessfullySubmitted(true)
      } catch (error) {
        toast.error(t('error:an_error_occurred'))
      }
    },
    [headers, id, t]
  )

  React.useEffect(() => {
    if (successfullySubmitted) {
      navigate(`/${locale}/administrator/sensitive/regimes`)
    }
  }, [successfullySubmitted, navigate, locale])

  return { onSubmit }
}

export default function AdministratorRegime() {
  const t = useTranslatable()
  return (
    <SuspenseWrapper title={t('admin_regimes:regime')}>
      <PageContent />
    </SuspenseWrapper>
  )
}

function PageContent() {
  const { id } = useParams<{ id: string }>()
  const t = useTranslatable()
  const locale = useLocale()

  const { regime, regimeDefinitions } = useRegimeData(id!)
  const { onSubmit } = useFormSubmission(id!)
  const { degreesOptions, semestersOptions, facultyOptions, isLoading } = useRegimeOptions(
    regime?.chooseCriterias != null
  )

  const breadcrumbsItems = React.useMemo(
    () => [
      { page: `${t('admin_regimes:regimes')}`, path: `/${locale}/administrator/sensitive/regimes` },
      { page: regime?.name, path: `/${locale}/administrator/sensitive/regimes/${id}` },
    ],
    [t, locale, regime?.name, id]
  )

  const initialValues = React.useMemo((): FormValues => {
    if (!regime) return {} as FormValues

    return {
      name: regime.name || '',
      showMessage: regime.showMessage.toString(),
      message: regime.message || null,
      type: regime.type?.toString() || '',
      status: regime.status || false,
      allowLogin: regime.allowLogin?.toString() || '',
      allowChoose: regime.allowChoose?.toString() || '',
      schedule: regime.schedule?.toString() || '',
      survey: regime.survey?.toString() || '',
      allowLecturerSurvey: regime.allowLecturerSurvey?.toString() || '',
      allowLecturerScore: regime.allowLecturerScore?.toString() || '',
      limitFunctional: regime.limitFunctional ? 'true' : 'false',
      chooseAllowed: regime.chooseAllowed ? 'true' : 'false',
      chooseCriterias:
        regime.chooseCriterias?.map((criteria) => ({
          fac_id:
            criteria.fac_id?.map((item) => ({
              label: facultyOptions.find((option) => option.value === item?.toString())?.label || item?.toString(),
              value: item?.toString(),
            })) || [],
          degree:
            criteria.degree?.map((item) => ({
              label: degreesOptions.find((option) => option.value === item?.toString())?.label || item?.toString(),
              value: item?.toString(),
            })) || [],
          sem:
            criteria.sem?.map((item) => ({
              label:
                semestersOptions.find((option) => option.value.toString() === item?.toString())?.label?.toString() ||
                item?.toString(),
              value: item?.toString(),
            })) || [],
          date_start: criteria.date_start ? new Date(criteria.date_start) : '',
          date_end: criteria.date_end ? new Date(criteria.date_end) : '',
        })) || [],
    }
  }, [regime, facultyOptions, degreesOptions, semestersOptions])

  if (!regime || !regimeDefinitions) {
    return <Loader className="mx-auto my-5 flex" />
  }
  return (
    <Section title={t('admin_regimes:regime')} breadcrubms={breadcrumbsItems}>
      <Form initialValues={initialValues} onSubmit={onSubmit} enableReinitialize isConfirmable>
        <FormContent
          degreesOptions={degreesOptions}
          semestersOptions={semestersOptions}
          facultyOptions={facultyOptions}
          isLoading={isLoading}
          regimeDefinitions={regimeDefinitions}
        />
      </Form>
    </Section>
  )
}

// Component for radio button groups
interface RadioGroupProps {
  name: string
  label: string
  options: Array<{ label: string; value: string | number }>
}

function RadioGroup({ name, label, options }: RadioGroupProps) {
  return (
    <div className="mb-[20px] flex w-full flex-col rounded-card">
      <label className="pb-2 font-bold text-primaryTextColor">{label}</label>
      <div role="group" aria-labelledby="my-radio-group">
        {options.map((option, index) => (
          <label key={option.value} className={index === 0 ? 'mr-4' : ''}>
            <Field type="radio" name={name} value={option.value} />
            <span className="ml-2 text-primaryTextColor">{option.label}</span>
          </label>
        ))}
      </div>
    </div>
  )
}

// Component for criteria item
interface CriteriaItemProps {
  index: number
  criteria: FormValues['chooseCriterias'][0]
  degreesOptions: SelectedOption[]
  semestersOptions: SelectedOption[]
  facultyOptions: SelectedOption[]
  onDelete: (index: number) => void
}

function CriteriaItem({
  index,
  criteria,
  degreesOptions,
  semestersOptions,
  facultyOptions,
  onDelete,
}: CriteriaItemProps) {
  const t = useTranslatable()

  return (
    <li className="mt-4 flex w-full flex-col items-end rounded-card bg-card p-4 shadow-md">
      <div className="flex w-full flex-col">
        <div className="flex w-full flex-row items-start justify-between">
          <span className="pr-4 text-lg font-bold text-primaryTextColor">{index + 1}</span>
          <Button onClick={() => onDelete(index)} type="button" variant="red" className="flex w-fit items-center gap-2">
            <DeleteIcon color="white" />
          </Button>
        </div>
        <div className="flex w-full flex-col">
          <SelectInput
            placeholder={t('faculty:faculty_name')}
            name={`chooseCriterias.${index}.fac_id`}
            options={facultyOptions}
            isMulti
            // required
            hideSelectedOptions
            label={t('faculty:faculty_name')}
            closeMenuOnSelect={false}
          />
          <SelectInput
            placeholder={t('person:degree')}
            name={`chooseCriterias.${index}.degree`}
            options={degreesOptions}
            isMulti
            // required
            hideSelectedOptions
            label={t('person:degree')}
            closeMenuOnSelect={false}
          />
          <SelectInput
            placeholder={t('common:semester')}
            name={`chooseCriterias.${index}.sem`}
            options={semestersOptions || []}
            isMulti
            // required
            label={t('common:semester')}
            closeMenuOnSelect={false}
            hideSelectedOptions
          />
          <div className="flex w-full items-center justify-between gap-4">
            <DateInput
              className="w-full"
              required
              name={`chooseCriterias.${index}.date_start`}
              label={t('common:start_date')}
              timePicker
              maxDate={criteria.date_end ? new Date(criteria.date_end) : undefined}
            />
            <DateInput
              className="w-full"
              required
              name={`chooseCriterias.${index}.date_end`}
              label={t('common:end_date')}
              timePicker
              minDate={criteria.date_start ? new Date(criteria.date_start) : undefined}
            />
          </div>
        </div>
      </div>
    </li>
  )
}

// Component for criteria list
interface CriteriaListProps {
  degreesOptions: SelectedOption[]
  semestersOptions: SelectedOption[]
  facultyOptions: SelectedOption[]
  isLoading: boolean
}

function CriteriaList({ degreesOptions, semestersOptions, facultyOptions, isLoading }: CriteriaListProps) {
  const t = useTranslatable()
  const { values } = useFormikContext<FormValues>()

  const handleDelete = React.useCallback(
    async (index: number, remove: (index: number) => void) => {
      const alert = await Swal.fire({
        title: t('common:do_you_really_want_to_delete'),
        icon: 'warning',
        showCancelButton: true,
        ...SWAL_CONFIG,
        confirmButtonText: t('common:confirm'),
        cancelButtonText: t('common:cancel'),
      })

      if (alert.isConfirmed) {
        remove(index)
      }
    },
    [t]
  )

  if (isLoading) {
    return <Loader className="mx-auto my-5 flex" />
  }

  if (!values.chooseCriterias) {
    return null
  }

  return (
    <FieldArray name="chooseCriterias">
      {({ remove, push }: FieldArrayRenderProps) => (
        <div className="mb-[20px]">
          <ul>
            <label className="font-bold text-primaryTextColor">{t('admin_regimes:choice_criterias')}</label>
            {values.chooseCriterias.map((criteria, index: number) => (
              <CriteriaItem
                key={index}
                index={index}
                criteria={criteria}
                degreesOptions={degreesOptions}
                semestersOptions={semestersOptions}
                facultyOptions={facultyOptions}
                onDelete={(index) => handleDelete(index, remove)}
              />
            ))}
            <Button
              variant="blue"
              type="button"
              className="my-4 flex w-full items-center justify-center"
              onClick={() =>
                push({
                  fac_id: [],
                  degree: [],
                  sem: [],
                  date_start: '',
                  date_end: '',
                })
              }
            >
              <Plus color="white" /> <span className="ml-2 mt-1">{t('common:add_more')}</span>
            </Button>
          </ul>
        </div>
      )}
    </FieldArray>
  )
}

interface FormContentProps {
  degreesOptions: SelectedOption[]
  semestersOptions: SelectedOption[]
  facultyOptions: SelectedOption[]
  isLoading: boolean
  regimeDefinitions: Api.RegimeDefinitions
}

function FormContent({
  degreesOptions,
  semestersOptions,
  facultyOptions,
  isLoading,
  regimeDefinitions,
}: FormContentProps): JSX.Element {
  const t = useTranslatable()
  const { values } = useFormikContext<FormValues>()
  const canManageRegimes = useAdminAccess().canManage('regimes')

  const showMessageOptions = [
    { label: t('common:yes'), value: '1' },
    { label: t('common:no'), value: '0' },
  ]

  const limitFunctionalOptions = [
    { label: t('common:yes'), value: 'true' },
    { label: t('common:no'), value: 'false' },
  ]

  return (
    <fieldset disabled={isLoading || !canManageRegimes}>
      {values.name != null && (
        <div className="mb-[20px]">
          <TextInput name="name" label={t('common:name')} type="text" required />
        </div>
      )}

      <SelectInput
        placeholder={t('common:type')}
        name="type"
        options={regimeDefinitions?.types}
        required
        label={t('common:type')}
        disabled
        className="cursor-not-allowed opacity-40"
      />

      <SelectInput
        placeholder={t('admin_regimes:allow_authorization')}
        name="allowLogin"
        options={regimeDefinitions?.allowLogin}
        required
        label={t('admin_regimes:allow_authorization')}
      />

      <SelectInput
        placeholder={t('admin_regimes:allow_choose')}
        name="allowChoose"
        options={regimeDefinitions?.allowChoose}
        required
        label={t('admin_regimes:allow_choose')}
      />

      <SelectInput
        placeholder={t('common:schedule')}
        name="schedule"
        options={regimeDefinitions?.schedules}
        required
        label={t('common:schedule')}
      />

      <SelectInput
        placeholder={t('admin_regimes:survey')}
        name="survey"
        options={regimeDefinitions?.surveys}
        required
        label={t('admin_regimes:survey')}
      />

      <SelectInput
        placeholder={t('admin_regimes:survey_lector')}
        name="allowLecturerSurvey"
        options={regimeDefinitions?.surveysLector}
        required
        label={t('admin_regimes:survey_lector')}
      />

      <SelectInput
        placeholder={t('admin_regimes:score_lector')}
        name="allowLecturerScore"
        options={regimeDefinitions?.scoresLector}
        required
        label={t('admin_regimes:score_lector')}
      />

      {values.showMessage != null && (
        <RadioGroup name="showMessage" label={t('admin_regimes:show_message')} options={showMessageOptions} />
      )}

      {values.message != null && (
        <div className="mb-[20px]">
          <TextareaInput name="message" label={t('admin_regimes:message')} rows={5} />
        </div>
      )}

      {values.limitFunctional != null && (
        <RadioGroup
          name="limitFunctional"
          label={t('admin_regimes:functional_limited')}
          options={limitFunctionalOptions}
        />
      )}

      <CriteriaList
        degreesOptions={degreesOptions}
        semestersOptions={semestersOptions}
        facultyOptions={facultyOptions}
        isLoading={isLoading}
      />

      {canManageRegimes && (
        <FormSubmit
          label={t('common:save')}
          classNames={{
            root: 'w-full flex justify-end border-t border-borderGrey py-4',
            button: 'h-[48px]',
          }}
        />
      )}
      <FormError />
    </fieldset>
  )
}
